import { getStrategy } from "./validate.study.plan.strategy";
import { getSubjectsParameter } from "../utils/helpers";

/**
 * Validates a study plan using scenario-specific strategies
 * @param {Object} studyPlan - The study plan to validate
 * @param {string} studyPlanType - The type of study plan (SCENARIO_A or SCENARIO_B)
 * @param {Object} study - The parent study object containing all study plans
 * @returns {Object} Object containing errors and warnings
 */

export const validateStudyPlan = (studyPlan, studyPlanType, study) => {
  const errors = {};
  const warnings = {};

  // Validate required fields
  validateRequiredFields(studyPlan, study, errors);

  // Validate overall values and seasonal variations
  Object.assign(warnings, validateAllOverallValues(studyPlan, studyPlanType));
  validateSeasonalVariationsErrors(studyPlan, errors);

  // Extract patient numbers from milestones and parameters
  const patientNumbers = extractPatientNumbers(studyPlan);

  // Validate patient numbers
  validatePatientNumbers(patientNumbers, errors);

  // Validate pause enrollment
  validatePauseEnrollmentErrors(studyPlan, patientNumbers, errors);

  return { errors, warnings };
};

/**
 * Validates required fields (name and description)
 */
const validateRequiredFields = (studyPlan, study, errors) => {
  if (!studyPlan?.studyPlanName) {
    errors.studyPlanName = "This field is required.";
    return;
  }

  // Check if study plan name is unique
  const isNameUnique = !study?.studyPlans?.some(
    (sp) =>
      sp.studyPlanId !== studyPlan.studyPlanId &&
      sp?.studyPlanName?.toLowerCase().trim() ===
        studyPlan.studyPlanName?.toLowerCase().trim()
  );

  if (!isNameUnique) {
    errors.studyPlanName = "Study Plan Name must be unique";
  }

  if (!studyPlan?.studyPlanDescr) {
    errors.studyPlanDescr = "This field is required.";
  }
};

/**
 * Validates seasonal variations and adds errors if invalid
 */
const validateSeasonalVariationsErrors = (studyPlan, errors) => {
  const { nonZeroValid } = validateSeasonalVariations(studyPlan);
  if (!nonZeroValid) {
    errors.NON_ZERO_SEASONAL =
      "At least one month for each country must have a non-zero value for Country Enrollment Seasonal Variations.";
    errors.BUTTON_TYPE = "ALL";
  }
};

/**
 * Extracts patient numbers from milestones and parameters
 */
const extractPatientNumbers = (studyPlan) => {
  const numbers = {
    screened: 0,
    enrolled: 0,
    completedTreatment: 0,
    completedFollowUp: 0,
  };

  studyPlan.milestones.forEach((milestone) => {
    switch (milestone.milestoneName) {
      case "NUM_SUBJECTS_ENROLLED":
        numbers.enrolled = milestone.lowerBound;
        break;
      case "NUM_SUBJECTS_TREATED":
        numbers.completedTreatment = milestone.lowerBound;
        break;
      case "NUM_SUBJECTS_COMPLETED":
        numbers.completedFollowUp = milestone.lowerBound;
        break;
      default:
        break;
    }
  });

  const subjectsToScreenParam = getSubjectsParameter(studyPlan?.parameters);
  if (subjectsToScreenParam) {
    numbers.screened = subjectsToScreenParam.lowerBound;
  }

  return numbers;
};

/**
 * Gets patient numbers validation errors
 * @param {Object} patientNumbers - Object with screened, enrolled, completedTreatment, completedFollowUp
 * @returns {Array<{key: string, message: string}>} Array of error objects, empty if valid
 */
function getPatientNumbersValidation(patientNumbers) {
  const errors = [];
  const { screened, enrolled, completedTreatment, completedFollowUp } = patientNumbers;

  // Validate counts > 0
  if (screened <= 0) {
    errors.push({
      key: 'PATIENT_NUM_VALID1',
      message: 'Number of participants screened must be greater than 0',
    });
  }

  if (enrolled !== null && enrolled <= 0) {
    errors.push({
      key: 'PATIENT_NUM_VALID2',
      message: 'Number of participants enrolled must be greater than 0',
    });
  }

  if (completedTreatment !== null && completedTreatment <= 0) {
    errors.push({
      key: 'PATIENT_NUM_VALID3',
      message: 'Number of participants completing treatment must be greater than 0',
    });
  }

  if (completedFollowUp !== null && completedFollowUp <= 0) {
    errors.push({
      key: 'PATIENT_NUM_VALID4',
      message: 'Number of participants completing follow up must be greater than 0',
    });
  }

  // Validate relationships
  if (enrolled > screened && screened > 0) {
    errors.push({
      key: 'PATIENT_COUNT1',
      message: "Number of participants enrolled shouldn't be greater than the number of participants screened",
    });
  }

  if (
    (completedTreatment > enrolled && enrolled > 0) ||
    (completedTreatment > screened && screened > 0)
  ) {
    errors.push({
      key: 'PATIENT_COUNT2',
      message: "Number of participants completing treatment shouldn't be greater than the number of participants screened or enrolled",
    });
  }

  if (
    (completedFollowUp > completedTreatment && completedTreatment > 0) ||
    (completedFollowUp > enrolled && enrolled > 0) ||
    (completedFollowUp > screened && screened > 0)
  ) {
    errors.push({
      key: 'PATIENT_COUNT3',
      message: "Number of participants completing follow up shouldn't be greater than the number of participants completing treatment, screened or enrolled",
    });
  }

  return errors;
}

/**
 * Validates patient numbers (counts and relationships)
 */
const validatePatientNumbers = (patientNumbers, errors) => {
  const validationErrors = getPatientNumbersValidation(patientNumbers);
  
  validationErrors.forEach(err => {
    errors[err.key] = err.message;
    errors.BUTTON_TYPE = "ALL";
  });
};

/**
 * Validates pause enrollment configuration
 */
const validatePauseEnrollmentErrors = (studyPlan, patientNumbers, errors) => {
  const { isInvalid, message } = getPauseEnrollmentValidation(
    patientNumbers.enrolled,
    {
      pauseEnrollmentAfterPatient: studyPlan.pauseEnrollmentAfterPatient,
      pauseEnrollmentDays: studyPlan.pauseEnrollmentDays,
    },
    studyPlan.screenFailRate,
    patientNumbers.screened
  );

  if (isInvalid) {
    errors.PAUSE_BIGGER_THAN_ENROLLED_SUBJECTS = message;
    errors.BUTTON_TYPE = "ALL";
  }
};


/**
 * Validates all overall values using the provided strategy
 * @param {Object} studyPlan - The study plan to validate
 * @param {Object} studyPlanType - The type of the study plan
 * @returns {boolean} True if validation passes, false otherwise
 */
/**
 * Validates seasonal variations in the study plan
 * @param {Object} studyPlan - The study plan to validate
 * @returns {Object} Object containing seasonalValid and nonZeroValid flags
 */
export function validateSeasonalVariations(studyPlan) {
  let seasonalValid = true;
  let nonZeroValid = true;
  const countries = studyPlan.countries;
  countries.forEach((country) => {
    country.siteGroups.forEach((siteGroup) => {
      let nonZeroCount = 0;
      siteGroup.enrollmentVariations.forEach((ev) => {
        const value = (ev.enrollmentSeasonalVariation ?? "").toString();
        const floatValue = parseFloat(value);
        if (value === "" ||
          isNaN(value) ||
          floatValue > 1 ||
          floatValue < 0
        ) {
          seasonalValid = false;
        } 
        if (floatValue > 0) {
          nonZeroCount += 1;
        }
      });
      if (nonZeroCount === 0) {
        nonZeroValid = false;
      }
    });
  });
  return { seasonalValid, nonZeroValid };
};

/**
 * Validates pause duration based on enrollment and screen fail rate
 * @param {number} numberPatientsEnrolled - Number of participants enrolled
 * @param {Object} pauseEnrollment - Pause enrollment details
 * @param {number} screenFailRate - Screen fail rate
 * @param {number} numberOfPatientsScreened - Lower bound of subjects to screen
 * @returns {boolean} True if validation passes, false otherwise
 */
export function validatePauseDuration(
  numberPatientsEnrolled,
  pauseEnrollment,
  screenFailRate,
  numberOfPatientsScreened,
) {
  const screenedPercentage = numberOfPatientsScreened * (1 - screenFailRate);
  const subjectsReference = Number((numberPatientsEnrolled) ? numberPatientsEnrolled : screenedPercentage);
  const pauseEnrollmentAfterPatient = Number(pauseEnrollment?.pauseEnrollmentAfterPatient);
  return pauseEnrollment?.pauseEnrollmentDays && (pauseEnrollmentAfterPatient >= subjectsReference);
};

/**
 * Gets pause enrollment validation result with message
 * @param {number} numberPatientsEnrolled - Number of participants enrolled
 * @param {Object} pauseEnrollment - Pause enrollment details
 * @param {number} screenFailRate - Screen fail rate
 * @param {number} numberOfPatientsScreened - Lower bound of subjects to screen
 * @returns {Object} Object with isInvalid boolean and message string
 */
export function getPauseEnrollmentValidation(
  numberPatientsEnrolled,
  pauseEnrollment,
  screenFailRate,
  numberOfPatientsScreened,
) {
  const isInvalid = validatePauseDuration(
    numberPatientsEnrolled,
    pauseEnrollment,
    screenFailRate,
    numberOfPatientsScreened
  );
  
  return {
    isInvalid,
    message: '"Pause Enrollment After Participant #" must be less than the total Number of Participants Enrolled.',
  };
};

/**
 * Validates overall values using the provided strategy
 * @param {Object} studyPlan - The study plan to validate
 * @param {Object} strategy - Strategy object with validation method
 * @returns {boolean} True if validation passes, false otherwise
 */
const validateOverallParameters = (studyPlan, strategy) => {
  const { overallSubjectsToScreen, emptyCountrySubjectToScreen } = getParameterCounts(studyPlan);
  const isEmptySubjectsToScreen = strategy.validations.validateSubjectsToScreen(
    overallSubjectsToScreen,
    emptyCountrySubjectToScreen,
  );
  return { isEmptySubjectsToScreen };
};

/**
 * Extracts enrollment rate parameters used for overall value validation
 * @param {Object} studyPlan - The study plan to extract values from
 * @returns {Object} Object with overallSubjectsToScreen and emptyCountrySubjectToScreen
 */
const getParameterCounts = (studyPlan) => {
  const overallSubjectsToScreen = parseFloat(studyPlan?.enrollmentRate) ?? 0;
  let emptyCountrySubjectToScreen = false;

  studyPlan.countries.forEach((country) => {
    country.siteGroups.forEach((siteGroup) => {
      if (!emptyCountrySubjectToScreen) {
        const subjectToScreen = parseFloat(siteGroup.siteEnrollmentRateAtSIV) ?? 0;
        emptyCountrySubjectToScreen = subjectToScreen === 0 || isNaN(subjectToScreen);
      }
    });
  });
  return {
    overallSubjectsToScreen,
    emptyCountrySubjectToScreen,
  };
};

/**
 * Validates all overall values using the provided strategy
 * @param {Object} studyPlan - The study plan to validate
 * @param {Object} studyPlanType - The type of the study plan
 * @returns {boolean} True if validation passes, false otherwise
 */
const validateAllOverallValues = (studyPlan, studyPlanType) => {
  let warnings = {};
  const strategy = getStrategy(studyPlanType);
  const { isEmptySubjectsToScreen } = validateOverallParameters(studyPlan, strategy);
  const warningKey = strategy.messages.getWarningKey();

  if (isEmptySubjectsToScreen && studyPlan?.ActualStep !== 1) {
    const { warning, buttonType } = strategy.messages.getEmptySubjectsToScreenMessage();
    warnings = {
      ...warnings,
      [warningKey]: warning,
      BUTTON_TYPE: buttonType,
    };
  }
  return warnings;
};
